import { h, createContext } from 'preact';
import { useContext, useEffect, useState } from 'preact/hooks';
import GiftReggieService from './index';

const Context = createContext();

export const useWishlist = () => useContext(Context);

// Use event queue to prevent multiple requests for the same product/variant
const eventQueue = new Set();

const WishlistContext = ({ children }) => {
  const giftReggieService = new GiftReggieService();
  const [wishlist, setWishlist] = useState([]);
  const [initialFetchComplete, setInitialFetchComplete] = useState(false);
  const technicalDataEl = document.querySelector('[data-technical]');
  const isLoggedIn =
    technicalDataEl?.innerHTML &&
    JSON.parse(technicalDataEl.innerHTML)?.customer;

  useEffect(() => {
    if (!isLoggedIn) return;
    giftReggieService
      .getWishlist()
      .then((wishlistResult) => {
        setWishlist(wishlistResult.products);
        setInitialFetchComplete(true);
      })
      .catch((error) => {
        console.error('Failed to load wishlist', error);
      });
  }, []);

  useEffect(() => {
    if (!isLoggedIn || !initialFetchComplete) return;
    const handleAddToWishlistFromStorage = () => {
      const wishlistVariantId = Number(
        window.sessionStorage.getItem('addToWishlist'),
      );
      if (wishlistVariantId && wishlist) {
        const isAlreadyInWishlist = wishlist.find(
          (item) => item.variant_id === wishlistVariantId,
        );
        if (!isAlreadyInWishlist && !eventQueue.has(wishlistVariantId)) {
          eventQueue.add(wishlistVariantId);
          addToWishlist(wishlistVariantId);
          window.sessionStorage.removeItem('addToWishlist');
          eventQueue.delete(wishlistVariantId);
        } else {
          window.sessionStorage.removeItem('addToWishlist');
        }
      }
    };

    handleAddToWishlistFromStorage();
  }, [wishlist, initialFetchComplete]);

  const addToWishlist = async (variantId) => {
    try {
      const updatedWishlist = await giftReggieService.addToWishlist(variantId);
      setWishlist(updatedWishlist.products);
    } catch (error) {
      console.warn('Failed to add product to wishlist', error);
    }
  };

  const removeFromWishlist = async (variantId) => {
    try {
      const updatedWishlist = await giftReggieService.removeFromWishlist(
        variantId,
      );
      setWishlist(updatedWishlist.products);
    } catch (error) {
      console.warn('Failed to remove product from wishlist', error);
    }
  };

  useEffect(() => {
    const handleAddToWishlist = (event) => {
      const variantId = `${event.detail.variantId}`;
      if (!eventQueue.has(variantId)) {
        eventQueue.add(variantId);
        addToWishlist(variantId).finally(() => {
          eventQueue.delete(variantId);
        });
      }
    };

    const handleRemoveFromWishlist = (event) => {
      const variantId = `${event.detail.variantId}`;
      if (!eventQueue.has(variantId)) {
        eventQueue.add(variantId);
        removeFromWishlist(variantId).finally(() => {
          eventQueue.delete(variantId);
        });
      }
    };

    window.addEventListener('addToWishlist', handleAddToWishlist);
    window.addEventListener('removeFromWishlist', handleRemoveFromWishlist);

    // Cleanup after component unmount
    return () => {
      window.removeEventListener('addToWishlist', handleAddToWishlist);
      window.removeEventListener(
        'removeFromWishlist',
        handleRemoveFromWishlist,
      );
    };
  }, []);

  return (
    <Context.Provider value={{ wishlist, addToWishlist, removeFromWishlist }}>
      {children}
    </Context.Provider>
  );
};

export const withWishlistContext = (Rendered) => (props) =>
  (
    <Context.Consumer>
      {(value) => <Rendered {...props} {...value} />}
    </Context.Consumer>
  );

export default WishlistContext;

const giftReggieService = new GiftReggieService();
window.TMWGetWishlist = giftReggieService.getWishlist;
